// Google Maps Platform Map Tiles API — mapa-base oficial do FloraIQ.
//
// Cada tipo de mapa exige uma sessao (POST /v1/createSession) e os tiles sao
// pedidos com esse token (`session=`). A sessao dura ~2 semanas e fica em
// cache no localStorage. Tiles em alta resolucao: 512 px (scaleFactor2x +
// highDpi) desenhados em caixas de 256, nitidos em tela de alta densidade.
//
// Sem chave, ou com a API recusando, o chamador cai no OpenStreetMap. URLs
// nao oficiais do Google (mt*.google.com) nao sao usadas: nao tem SLA e violam
// os termos de uso.

export const GOOGLE_MAPS_API_KEY: string = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined)?.trim() ?? "";

export type GoogleMapMode = "hybrid" | "satellite" | "roadmap" | "roadmap_clean";

export type GoogleSession = { session: string; expiry: string; tileWidth?: number };

const TILES = "https://tile.googleapis.com";
const CACHE_PREFIX = "floraiq_web_tiles_v2_";
const inflight = new Map<GoogleMapMode, Promise<GoogleSession>>();

export const OSM_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
export const OSM_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>';

function sessionBody(mode: GoogleMapMode) {
  const body: Record<string, unknown> = {
    language: "pt-BR",
    region: "BR",
    scale: "scaleFactor2x",
    highDpi: true,
    mapType: mode === "satellite" || mode === "hybrid" ? "satellite" : "roadmap",
  };
  if (mode === "hybrid") body.layerTypes = ["layerRoadmap"];
  if (mode === "roadmap_clean") body.styles = [{ elementType: "labels", stylers: [{ visibility: "off" }] }];
  return body;
}

function readCache(mode: GoogleMapMode): GoogleSession | null {
  try {
    const cached = JSON.parse(localStorage.getItem(CACHE_PREFIX + mode) || "null") as GoogleSession | null;
    // expiry vem em segundos; renova com 1 h de folga.
    if (cached && Number(cached.expiry) * 1000 > Date.now() + 3_600_000) return cached;
  } catch {
    /* localStorage indisponivel: segue sem cache */
  }
  return null;
}

export function getGoogleSession(mode: GoogleMapMode): Promise<GoogleSession> {
  if (!GOOGLE_MAPS_API_KEY) return Promise.reject(new Error("SEM_CHAVE"));
  const cached = readCache(mode);
  if (cached) return Promise.resolve(cached);
  const pending = inflight.get(mode);
  if (pending) return pending;

  const request = fetch(`${TILES}/v1/createSession?key=${encodeURIComponent(GOOGLE_MAPS_API_KEY)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(sessionBody(mode)),
  })
    .then((r) => r.json())
    .then((d) => {
      if (!d?.session) throw new Error(d?.error?.status ?? "SEM_SESSAO");
      try {
        localStorage.setItem(CACHE_PREFIX + mode, JSON.stringify(d));
      } catch {
        /* sem cache */
      }
      return d as GoogleSession;
    })
    .finally(() => inflight.delete(mode));
  inflight.set(mode, request);
  return request;
}

export function googleTileUrl(session: GoogleSession): string {
  return `${TILES}/v1/2dtiles/{z}/{x}/{y}?session=${session.session}&key=${encodeURIComponent(GOOGLE_MAPS_API_KEY)}`;
}

/** Copyright exigido pela Google para a area visivel (endpoint viewport). */
export async function fetchGoogleCopyright(
  session: GoogleSession,
  zoom: number,
  bounds: { north: number; south: number; east: number; west: number },
): Promise<string> {
  const url =
    `${TILES}/tile/v1/viewport?session=${session.session}&key=${encodeURIComponent(GOOGLE_MAPS_API_KEY)}` +
    `&zoom=${zoom}&north=${bounds.north}&south=${bounds.south}&east=${bounds.east}&west=${bounds.west}`;
  try {
    const d = await (await fetch(url)).json();
    return typeof d?.copyright === "string" ? d.copyright : "";
  } catch {
    return "";
  }
}
